import { useState, useEffect, useRef } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  updateProfile,
  signOut as firebaseSignOut,
  onIdTokenChanged,
  type User as FirebaseUser
} from "firebase/auth";
import { auth } from "@/lib/firebase";
import apiClient, { setApiToken } from "@/lib/api-client";
import { logApiError, getErrorMessage } from "@/lib/errors";
import { refreshTokenIfNeeded } from "@/lib/token-refresh";
import { setUser as setSentryUser, clearUser as clearSentryUser } from "@/utils/sentry";

import { User } from "@/api/generated/client/models";

interface LoginCredentials {
  username: string;
  password: string;
}

interface SignupCredentials {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  claimInviteToken?: string;
}

export function useAuth() {
  const [firebaseUser, setFirebaseUser] = useState<FirebaseUser | null>(null);
  const [isAuthLoading, setIsAuthLoading] = useState(true);
  const queryClient = useQueryClient();
  const tokenRefreshIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Listen to Firebase auth state
  useEffect(() => {
    const unsubscribe = onIdTokenChanged(auth, async (user) => {
      setFirebaseUser(user);
      if (user) {
        // Update API token on every token change (login, refresh, expiry)
        const token = await user.getIdToken();
        setApiToken(token);
      } else {
        setApiToken('');
      }
      setIsAuthLoading(false);
    });
    return unsubscribe;
  }, []);

  // Proactive token refresh - check every 5 minutes and refresh if needed
  useEffect(() => {
    if (!firebaseUser) {
      // Clear interval if user logs out
      if (tokenRefreshIntervalRef.current) {
        clearInterval(tokenRefreshIntervalRef.current);
        tokenRefreshIntervalRef.current = null;
      }
      return;
    }

    // Check token expiration every 5 minutes
    const intervalMs = 5 * 60 * 1000; // 5 minutes
    
    tokenRefreshIntervalRef.current = setInterval(async () => {
      try {
        await refreshTokenIfNeeded(setApiToken, 5); // Refresh if expiring within 5 minutes
      } catch (error) {
        console.error('[useAuth] Proactive token refresh failed:', error);
      }
    }, intervalMs);

    // Also check immediately on mount
    refreshTokenIfNeeded(setApiToken, 5).catch(error => {
      console.error('[useAuth] Initial token refresh check failed:', error);
    });

    return () => {
      if (tokenRefreshIntervalRef.current) {
        clearInterval(tokenRefreshIntervalRef.current);
      }
    };
  }, [firebaseUser]);

  // Fetch user data from backend
  const { data: user, isLoading: isUserLoading } = useQuery({
    queryKey: ["user", firebaseUser?.uid],
    queryFn: async () => {
      if (!firebaseUser) return null;

      const token = await firebaseUser.getIdToken();
      setApiToken(token);

      // Use API client
      const response = await apiClient.users.getCurrentUser();
      return response as unknown as User;
    },
    enabled: !!firebaseUser,
  });

  // Set Sentry user context when user data is available
  useEffect(() => {
    if (user) {
      setSentryUser({
        id: user.id,
        email: user.email,
        name: user.name || undefined,
        companyId: user.companyId || undefined,
        role: user.role,
      });
    }
  }, [user]);

  // Login mutation
  const loginMutation = useMutation({
    mutationFn: async ({ username, password }: LoginCredentials) => {
      // Sign in with Firebase
      const userCredential = await signInWithEmailAndPassword(
        auth,
        username,
        password
      );

      // Get Firebase token and authenticate with backend
      const token = await userCredential.user.getIdToken();
      setApiToken(token);

      // Use API client
      try {
        const response = await apiClient.users.getCurrentUser();
        return response;
      } catch (error) {
        if (error instanceof Error && error.message.includes('interceptors did not return')) {
          throw new Error('Failed to connect to backend. Please ensure the server is running on port 3000.');
        }
        // Improve error message if possible
        if (error instanceof Error) {
          throw new Error(error.message || 'Login failed');
        }
        throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["user"] });
    },
  });

  // Signup mutation
  const signupMutation = useMutation({
    mutationFn: async ({ email, password, firstName, lastName, claimInviteToken }: SignupCredentials) => {
      // Create Firebase user
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        email,
        password
      );

      // Update profile with name so backend can use it
      await updateProfile(userCredential.user, {
        displayName: `${firstName} ${lastName}`.trim()
      });

      // Get Firebase token and register with backend
      // Force refresh to ensure token contains the updated displayName
      const token = await userCredential.user.getIdToken(true);
      setApiToken(token);

      try {
        // Use generated client for onboarding
        const response = await apiClient.onboarding.createCompanyWithOwner({
          createCompanyRequest: {
            name: `${firstName}'s Workspace`,
            ...(claimInviteToken ? { claimInviteToken } : {})
          }
        });
        return response;
      } catch (error: unknown) {
        logApiError(error, 'Signup onboarding');
        
        // If onboarding fails, we might still have the firebase user. 
        // Ideally we should rollback or handle this state. 
        // For now just throw clearer error with extracted message.
        const msg = getErrorMessage(error) || 'Failed to complete onboarding';
        clearSentryUser(); // Clear Sentry user context
        throw new Error(msg);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["user"] });
    },
  });

  // Logout mutation
  const logoutMutation = useMutation({
    mutationFn: async () => {
      await firebaseSignOut(auth);
      setApiToken(''); // Clear token
    },
    onSuccess: () => {
      queryClient.clear();
    },
  });

  return {
    user,
    firebaseUser,
    isLoading: isAuthLoading || isUserLoading,
    loginMutation,
    signupMutation,
    logoutMutation,
  };
}
